import { IconButton } from '@synoptic/ui-kit/button/icon-button.tsx';
import { Hint } from '@synoptic/ui-kit/hint/hint.js';
import { MinusIcon } from '@synoptic/ui-kit/icons/react/minus.tsx';
import { PlusIcon } from '@synoptic/ui-kit/icons/react/plus.tsx';
import { Wrap } from '@synoptic/ui-kit/wrap.js';
import { clsx } from 'clsx';
import {
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
  useRef,
  useState,
} from 'react';
import { sanitizeDecimalInput } from '@/components/stepper/sanitize-decimal-input.js';
import {
  leftButton,
  rightButton,
  type StepperVariant,
  stepperBody,
  stepperBodyVariants,
  stepperContainer,
  stepperContainerVariants,
  stepperInput,
  stepperInputVariants,
  stepperSuffix,
} from '@/components/stepper/stepper.css';

const VARIANT_CONFIG = {
  default: {
    iconButtonVariant: 'secondary',
    iconButtonSize: 'small',
    iconButtonRounded: false,
    iconButtonClassName: { left: leftButton, right: rightButton },
  },
  input: {
    iconButtonVariant: 'ghost',
    iconButtonSize: 'small',
    iconButtonRounded: false,
    iconButtonClassName: { left: leftButton, right: rightButton },
  },
} as const;

const roundTo = (value: number, decimals: number) => {
  if (decimals <= 0) return Math.round(value);
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

const formatValue = (value: number, decimals: number) => {
  if (decimals <= 0) return String(Math.round(value));
  return Number(value.toFixed(decimals)).toString();
};

const clampToStep = (
  value: number,
  step: number,
  minCount: number,
  maxCount: number,
  decimals: number,
) => {
  const safeStep = step > 0 ? step : 1;
  const minValid = Math.ceil(minCount / safeStep) * safeStep;
  const maxValid = Math.floor(maxCount / safeStep) * safeStep;
  if (!Number.isFinite(value)) return roundTo(minValid, decimals);
  const rounded = Math.round(value / safeStep) * safeStep;
  if (rounded < minValid) return roundTo(minValid, decimals);
  if (rounded > maxValid) return roundTo(maxValid, decimals);
  return roundTo(rounded, decimals);
};

export type StepperStep = number | ((count: number) => number);

export const Stepper = ({
  count,
  setCount,
  step,
  minCount,
  maxCount,
  disableIncrement,
  decimals = 0,
  suffix,
  placeholder,
  variant = 'default',
  commitOnType = true,
}: {
  count: number;
  setCount: (count: number) => void;
  step: StepperStep;
  minCount: number;
  maxCount: number;
  disableIncrement?: boolean | string;
  decimals?: number;
  suffix?: ReactNode;
  placeholder?: string;
  variant?: StepperVariant;
  // When false, typing only updates local state and the parent is notified
  // on blur — appropriate for coarse-step inputs where intermediate values
  // would render misleading derived state (e.g. step=5 target counts).
  commitOnType?: boolean;
}) => {
  const displayValue = (value: number) =>
    placeholder !== undefined && value === 0
      ? ''
      : formatValue(value, decimals);

  const [inputValue, setInputValue] = useState(displayValue(count));
  const [prevCount, setPrevCount] = useState(count);
  const [focused, setFocused] = useState(false);
  const skipNextCommitRef = useRef(false);

  const parseInput = (raw: string) =>
    decimals > 0 ? Number.parseFloat(raw) : Number.parseInt(raw, 10);

  if (count !== prevCount) {
    setPrevCount(count);
    // Skip reformat when typed text already parses to the new count —
    // preserves "8." / "8.50" while we round-trip through the parent.
    const parsed = parseInput(inputValue);
    if (!Number.isFinite(parsed) || parsed !== count) {
      setInputValue(displayValue(count));
    }
  }

  const config = VARIANT_CONFIG[variant];

  // Resolved per-call so tapered schedules (e.g. Kalshi) pick the right
  // tier based on the value being acted on, not the stale committed prop.
  const resolveStep = (forValue: number): number => {
    const resolved = typeof step === 'function' ? step(forValue) : step;
    return Number.isFinite(resolved) && resolved > 0 ? resolved : 1;
  };

  const effectiveMax = disableIncrement ? Math.min(maxCount, count) : maxCount;

  // Base ± off the in-flight input rather than the (possibly stale) parent
  // count — needed in blur-only mode where typing doesn't propagate, and
  // harmless in live mode where the two stay in sync.
  const parsedInput = parseInput(inputValue);
  const baseValue = Number.isFinite(parsedInput) ? parsedInput : count;

  // Snap to the target tier's grid so off-grid typed values and tier
  // crossings never produce a price the exchange would reject.
  const computeStepped = (direction: 1 | -1) => {
    const stepHere = resolveStep(baseValue);
    const target = roundTo(baseValue + direction * stepHere, decimals);
    const stepAtTarget = resolveStep(target);
    return clampToStep(target, stepAtTarget, minCount, effectiveMax, decimals);
  };

  const decrementValue = computeStepped(-1);
  const incrementValue = computeStepped(1);

  const commit = () => {
    setFocused(false);
    if (skipNextCommitRef.current) {
      skipNextCommitRef.current = false;
      setInputValue(displayValue(count));
      return;
    }
    const parsed = parseInput(inputValue);
    const stepForCommit = resolveStep(Number.isFinite(parsed) ? parsed : count);
    const next = clampToStep(
      parsed,
      stepForCommit,
      minCount,
      effectiveMax,
      decimals,
    );
    if (next === count) {
      setInputValue(displayValue(count));
    } else {
      setCount(next);
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const sanitized = sanitizeDecimalInput(event.target.value, decimals);
    setInputValue(sanitized);

    if (!commitOnType) return;
    if (sanitized === '') return;
    const parsed = parseInput(sanitized);
    if (!Number.isFinite(parsed) || parsed === count) return;
    // Hold back out-of-bounds intermediates (e.g. "150" while typing toward
    // a max of 100) so parent state never sees a value commit would reject.
    if (parsed < minCount || parsed > effectiveMax) return;

    setCount(parsed);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.blur();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      skipNextCommitRef.current = true;
      event.currentTarget.blur();
    }
  };

  return (
    <div className={clsx(stepperContainer, stepperContainerVariants[variant])}>
      <IconButton
        type={'button'}
        variant={config.iconButtonVariant}
        size={config.iconButtonSize}
        rounded={config.iconButtonRounded}
        className={config.iconButtonClassName.left}
        onClick={() => setCount(decrementValue)}
        disabled={decrementValue >= baseValue}
      >
        <MinusIcon />
      </IconButton>
      <div className={clsx(stepperBody, stepperBodyVariants[variant])}>
        <input
          type="text"
          inputMode={decimals > 0 ? 'decimal' : 'numeric'}
          pattern={decimals > 0 ? '[0-9.]*' : '[0-9]*'}
          className={clsx(stepperInput, stepperInputVariants[variant])}
          value={inputValue}
          placeholder={focused ? undefined : placeholder}
          size={Math.max(inputValue.length, placeholder?.length ?? 0, 1)}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          onBlur={commit}
          onKeyDown={handleKeyDown}
          aria-label="Value"
        />
        {suffix !== undefined && (
          <span className={stepperSuffix}>{suffix}</span>
        )}
      </div>
      <Wrap
        with={Hint}
        if={typeof disableIncrement === 'string'}
        description={disableIncrement as string}
      >
        <IconButton
          type={'button'}
          variant={config.iconButtonVariant}
          size={config.iconButtonSize}
          rounded={config.iconButtonRounded}
          className={config.iconButtonClassName.right}
          onClick={() => setCount(incrementValue)}
          disabled={incrementValue <= baseValue || !!disableIncrement}
        >
          <PlusIcon />
        </IconButton>
      </Wrap>
    </div>
  );
};
